import { Appearance } from '@angular-material-extensions/google-maps-autocomplete';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AddressObject, Marker } from './types';


@Component({
    selector: 'app-google-geo',
    templateUrl: './google-geo.component.html',
    standalone: false
})
export class GoogleGeoComponent implements OnInit, OnDestroy, OnChanges {
    @Input() fullAddress?: string;
    @Input() addressLabel?: string;
    @Input() showMap = true;
    @Input() parsedAddress?: { address: string };
    @Input() inLatitude?: string;
    @Input() inLongitude?: string;
    @Input() mandatory?: boolean;
    @Output() addressEmitted: EventEmitter<AddressObject> = new EventEmitter<AddressObject>();
    @Output() errorEmitted: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('map', { static: true }) public mapElementRef: any;
    @ViewChild('autocomplete') autocomplete: any;


    public appearance = Appearance;
    public zoom?: number;
    marker?: Marker;
    private mapClickListener: any;
    private map: google.maps.Map | undefined;
    customMarker = false;
    latitude?: number;
    longitude?: number;
    private geocoder: google.maps.Geocoder | undefined;
    address: AddressObject = {};
    countries = ['cz', 'sk'];
    touched = false;
    isMapOpen = false;
    private placesService: google.maps.places.PlacesService | undefined;
    placeholderText = '';
    google: any;

    private isLocationSelected = false;

    mapOptions: google.maps.MapOptions = {
        disableDefaultUI: true,
        streetViewControl: false,
        zoomControl: true
    };

    constructor(
        private _formBuilder: FormBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
        private translateService: TranslateService,
        private zone: NgZone
    ) {
        window.onload = () => {
            const dummyDiv = document.createElement('div');
            this.placesService = new google.maps.places.PlacesService(dummyDiv);
        };

    }

    async ngOnInit(): Promise<void> {

        this.google = google;
        this.placeholderText = this.translateService.instant('section.geo.placeholder');
        this._changeDetectorRef.detectChanges();
        this.zoom = 7;
        this.latitude = 49.66056346200483;
        this.longitude = 15.390425091058537;
        this.renderMap();
        if (this.fullAddress) {
            this.address.formattedAddress = this.fullAddress;
            if (this.inLatitude && this.inLongitude) {
                this.latitude = +this.inLatitude;
                this.longitude = +this.inLongitude;
                this.marker = {
                    lat: this.latitude,
                    lng: this.longitude,
                    draggable: false
                };
            }
            this._changeDetectorRef.detectChanges();
        } else {
            if (this.mandatory) {
                this.errorEmitted.emit(true);

            }
        }

    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.parsedAddress && !changes.parsedAddress.firstChange) {
            this.onFullAddressChange();
        }
    }

    onFullAddressChange(address?: string) {
        let addressQuery = this.parsedAddress?.address;

        if (address) {
            addressQuery = address;
        }

        const requestTest = {
            query: addressQuery
        };
        this._changeDetectorRef.markForCheck();

        if (!this.placesService) {
            this.placesService = new google.maps.places.PlacesService(document.createElement('div'));
            this._changeDetectorRef.markForCheck();
        }
        // Use the PlacesService to perform the search
        this.placesService?.textSearch(requestTest, (resultsText: any[], statusTest: string) => {
            this._changeDetectorRef.markForCheck();

            this.zone.run(() => {
                this._changeDetectorRef.markForCheck();
                if (statusTest === google.maps.places.PlacesServiceStatus.OK) {
                    const requestDetail = {
                        placeId: resultsText[0]?.place_id
                    };
                    this._changeDetectorRef.markForCheck();
                    this.placesService!.getDetails(requestDetail, (result, status) => {
                        this._changeDetectorRef.markForCheck();
                        this.zone.run(() => {
                            this._changeDetectorRef.markForCheck();
                            if (status === google.maps.places.PlacesServiceStatus.OK) {
                                this.address.formattedAddress = undefined;
                                this._changeDetectorRef.detectChanges();

                                this.address.formattedAddress = result?.formatted_address;
                                this.address.placeId = result?.place_id;
                                this.address.addressComponents = result?.address_components;

                                if (result?.geometry && result?.geometry.location) {
                                    const location = result.geometry.location;
                                    this.latitude = location.lat();
                                    this.longitude = location.lng();
                                    if (this.map) {
                                        this.map.setCenter(location);
                                        this.animateMapZoomTo(this.map, 12);
                                    }
                                    if (this.latitude && this.longitude) {
                                        this.marker = {
                                            lat: this.latitude,
                                            lng: this.longitude,
                                            draggable: false
                                        };
                                    }
                                    this.address.latitude = this.latitude;
                                    this.address.longitude = this.longitude;
                                }
                                this._changeDetectorRef.markForCheck();
                                this.emitAddress();
                            } else {
                                console.error('Error in PlacesService:', status);
                            }
                        });
                    });

                    this._changeDetectorRef.detectChanges();
                } else {
                    console.error(status);
                }
            });
        });
    }

    ngOnDestroy(): void {
        if (this.mapClickListener) {
            google.maps.event.removeListener(this.mapClickListener);
        }
    }

    renderMap(): void {
        if (this.mapElementRef) {
            this.mapElementRef.triggerResize();
            this._changeDetectorRef.detectChanges();
        }
    }

    onAutocompleteSelected(result: google.maps.places.PlaceResult) {
        this.isLocationSelected = true;

        this.customMarker = false;
        this.address.formattedAddress = result?.formatted_address;
        this.address.addressComponents = result?.address_components;
        this.address.placeId = result?.place_id;

        setTimeout(() => {
            this.isLocationSelected = false;
        }, 200);

    }

    onLocationSelected(location: any) {
        this.isLocationSelected = true;

        this.latitude = location.latitude;
        this.longitude = location.longitude;
        if (this.map) {
            this.animateMapZoomTo(this.map, 12);
        }
        this.marker = {
            lat: location.latitude,
            lng: location.longitude,
            draggable: false
        };
        this.address.latitude = location.latitude;
        this.address.longitude = location.longitude;
        this.emitAddress();
        setTimeout(() => {
            this.isLocationSelected = false;
        }, 200);

        this._changeDetectorRef.markForCheck();
    }

    clickedMarker() {
        if (this.marker) {
            // Možnost odstranění markeru při kliku na něj.
            // this.marker = undefined;
        }
        this._changeDetectorRef.detectChanges();
    }

    public mapReadyHandler(map: google.maps.Map): void {
        this.map = map;
        if (this.fullAddress) {
            this.animateMapZoomTo(this.map, 12);
        }
        this.mapClickListener = this.map.addListener('click', (e: google.maps.MapMouseEvent) => {
            this.zone.run(() => {
                if (this.customMarker) {
                    this.marker = {
                        lat: e.latLng!.lat(),
                        lng: e.latLng!.lng(),
                        draggable: false
                    };
                    this.address.latitude = e.latLng!.lat();
                    this.address.longitude = e.latLng!.lng();
                    this.geocodeLatLng();
                    this._changeDetectorRef.markForCheck();
                }
            });
        });
    }

    toggleMap() {
        this.isMapOpen = !this.isMapOpen;
        this._changeDetectorRef.markForCheck();
    }

    geocodeLatLng() {
        if (this.marker) {
            const latlng = {
                lat: this.marker.lat,
                lng: this.marker.lng
            };
            this.geocoder = new google.maps.Geocoder();
            this.geocoder.geocode({ location: latlng }, (results, status) => {
                if (status === google.maps.GeocoderStatus.OK) {
                    this.address.formattedAddress = results[0].formatted_address;
                    this.address.addressComponents = results[0].address_components;
                    this.address.placeId = results[0].place_id;
                    this.emitAddress();
                } else {
                    this.emitAddress();
                    console.error('Geocode was not successful: ' + status);
                }
            });
        }
    }

    emitAddress() {
        this.addressEmitted.emit(this.address);
        if (!this.address.latitude && this.touched) {
            this.errorEmitted.emit(true);
        } else {
            this.errorEmitted.emit(false);
        }
    }

    addressTouched() {
        this.touched = true;
        this.address.latitude = undefined;
        this.errorEmitted.emit(true);
        this._changeDetectorRef.markForCheck();
    }

    animateMapZoomTo(map: google.maps.Map, targetZoom: number, currentZoom?: number) {
        currentZoom = currentZoom || map.getZoom();
        if (currentZoom !== targetZoom && currentZoom !== undefined) {
            google.maps.event.addListenerOnce(map, 'zoom_changed', () => {
                if (currentZoom) {
                    this.animateMapZoomTo(map, targetZoom, currentZoom + (targetZoom > currentZoom ? 1 : -1));
                }
            });
            setTimeout(() => {
                if (currentZoom) {
                    map.setZoom(currentZoom);
                }
            }, 80);
        }
    }

    lostFocus() {
        setTimeout(() => {

            if (this.isLocationSelected) {
                return;
            } else {
                if (this.autocomplete?.address) {
                    this.onFullAddressChange(this.autocomplete?.address);
                }
            }
            this.isLocationSelected = false;

        }, 100);

    }

}
